// The one widget shell every ORDER Orb widget renders through.
// compact  = one line: DEITY · summary (+ status mark)
// expanded = DEITY · TITLE header, alert, inline metrics, dense lines / body, actions, agent.
// No card chrome — widgets sit straight on the orb. Status: quiet = low contrast ·
// attention = amber word + alert · critical = restrained red word + alert.
import React from 'react';
import { Pressable, Text, View } from 'react-native';
import { DISPLAY_FONT, useV10 } from '../../theme';
import { Btn, useK } from '../ui';
import type { OrbWidgetMetric, OrbWidgetModel, OrbWidgetStatus } from './types';

export interface ShellProps {
  model: OrbWidgetModel;
  mode: 'compact' | 'expanded';
  /** Tap on the header / compact line. */
  onToggle?: () => void;
  /** Open the deity chat for this widget (absent = no agent affordance shown). */
  onAgent?: (agentId: string, widgetId: string) => void;
  pinned?: boolean;
  onPin?: () => void;
  /** Extra content under the body (the section's level lens). */
  footer?: React.ReactNode;
}

function useStatusColors(s: OrbWidgetStatus) {
  const t = useV10();
  switch (s) {
    case 'critical':
      return { text: t.maladyText, alpha: 1 };
    case 'attention':
      return { text: t.behind, alpha: 1 };
    case 'active':
      return { text: t.goldBright, alpha: 1 };
    default:
      return { text: t.ivoryFaint, alpha: 0.82 };
  }
}

function Metrics({ metrics }: { metrics: OrbWidgetMetric[] }) {
  const t = useV10();
  const k = useK();
  const tone = (m: OrbWidgetMetric) => (m.tone === 'positive' ? t.onPace : m.tone === 'warning' ? t.behind : m.tone === 'critical' ? t.maladyText : t.ivory);
  return (
    <Text style={{ fontSize: 11.5 * k, lineHeight: 15 * k, color: t.ivorySoft }} numberOfLines={2}>
      {metrics.map((m, i) => (
        <Text key={m.label}>
          {i ? <Text style={{ color: t.ivoryFaint }}>{'  ·  '}</Text> : null}
          {m.label} <Text style={{ color: tone(m), fontWeight: '700' }}>{m.value}</Text>
        </Text>
      ))}
    </Text>
  );
}

export function WidgetShell({ model: m, mode, onToggle, onAgent, pinned, onPin, footer }: ShellProps) {
  const t = useV10();
  const k = useK();
  const sc = useStatusColors(m.status);
  // No card: widgets sit directly on the orb's marble. Status reads from the
  // header word and the alert colour, not from a frame.
  const frame = { opacity: sc.alpha };
  const agent = onAgent && m.agentId ? (
    <Pressable onPress={() => onAgent(m.agentId!, m.id)} hitSlop={6} accessibilityLabel={`Ask ${m.deityName}`} style={{ paddingHorizontal: 4 * k, outlineStyle: 'none' } as object}>
      <Text style={{ color: t.goldBright, fontSize: 12 * k }}>✦</Text>
    </Pressable>
  ) : null;

  if (mode === 'compact') {
    return (
      <Pressable onPress={onToggle} style={[frame, { flexDirection: 'row', alignItems: 'center', gap: 6 * k, paddingVertical: 3 * k }, { outlineStyle: 'none' } as object]}>
        <Text style={{ fontFamily: DISPLAY_FONT, color: t.goldBright, fontSize: 10.5 * k, letterSpacing: 1 * k }} numberOfLines={1}>
          {m.deityName}
        </Text>
        <Text style={{ flex: 1, color: m.empty ? t.ivoryFaint : t.ivory, fontSize: 11.5 * k, fontStyle: m.empty ? 'italic' : 'normal' }} numberOfLines={1}>
          {m.summary}
        </Text>
        {agent}
      </Pressable>
    );
  }

  const actions = [m.primaryAction, ...(m.secondaryActions ?? [])].filter(Boolean).slice(0, 4);
  return (
    <View style={[frame, { paddingVertical: 2 * k, gap: 4 * k }]}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 * k }}>
        <Pressable onPress={onToggle} disabled={!onToggle} style={[{ flex: 1 }, { outlineStyle: 'none' } as object]}>
          <Text style={{ fontFamily: DISPLAY_FONT, color: t.goldBright, fontSize: 11.5 * k, letterSpacing: 1.4 * k }} numberOfLines={1}>
            {m.deityName} <Text style={{ color: t.gold }}>·</Text> {m.title}
          </Text>
        </Pressable>
        {m.status !== 'quiet' && m.status !== 'active' ? (
          <Text style={{ color: sc.text, fontSize: 9 * k, fontWeight: '800', letterSpacing: 1 * k }}>{m.status.toUpperCase()}</Text>
        ) : null}
        {onPin ? (
          <Pressable onPress={onPin} hitSlop={6} accessibilityLabel={pinned ? 'Unpin' : 'Pin'} style={{ paddingHorizontal: 3 * k, outlineStyle: 'none' } as object}>
            <Text style={{ color: pinned ? t.goldBright : t.ivoryFaint, fontSize: 11 * k }}>{pinned ? '★' : '☆'}</Text>
          </Pressable>
        ) : null}
        {agent}
      </View>
      {m.empty ? (
        <Text style={{ color: t.ivoryFaint, fontStyle: 'italic', fontFamily: DISPLAY_FONT, fontSize: 12 * k, lineHeight: 16 * k }} numberOfLines={2}>
          {m.summary}
        </Text>
      ) : (
        <>
          {m.alert ? (
            <Text style={{ color: sc.text, fontSize: 11 * k, lineHeight: 14 * k, fontWeight: '700' }} numberOfLines={2}>
              {m.alert}
            </Text>
          ) : null}
          {m.metrics?.length ? <Metrics metrics={m.metrics} /> : null}
          {m.lines?.map((l, i) => (
            <Text key={i} style={{ color: i === 0 ? t.ivory : t.ivorySoft, fontSize: (i === 0 ? 12.5 : 11.5) * k, lineHeight: (i === 0 ? 16 : 15) * k, fontWeight: i === 0 ? '700' : '400' }} numberOfLines={1}>
              {l}
            </Text>
          ))}
          {m.body}
        </>
      )}
      {actions.length ? (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 5 * k, marginTop: 2 * k }}>
          {actions.map((a, i) => (
            <Btn key={`${a!.label}-${i}`} label={a!.label} tone={a!.tone ?? (i === 0 ? 'gold' : 'ghost')} disabled={a!.disabled} onPress={a!.run} />
          ))}
        </View>
      ) : null}
      {footer}
    </View>
  );
}
